import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import SingleOptimizeFlow from './aiResumeOptimizer/SingleOptimizeFlow';
import type { OptimizationResult } from './aiResumeOptimizer/shared/types';
import type { NextActionPage } from './aiResumeOptimizer/NextActionModal';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { getOptimizerFlowStepLabel } from './optimizerFlowBreadcrumb';

type RoleFitCtx = {
  candidateName: string;
  candidateEmail?: string;
  jobTitle: string;
  clientName?: string;
  candidateId?: string;
  jobId?: string;
  returnTo?: string;
};

const ROLEFIT_OPT_SESSION_KEY = 'ggj_rolefit_opt_ctx';

function clearRoleFitOptimizeSession() {
  try {
    sessionStorage.removeItem(ROLEFIT_OPT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function SingleOptimizePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const applicationIdFromUrl = searchParams.get('applicationId');
  const cidParam = searchParams.get('roleFitCandidateId') ?? '';
  const jidParam = searchParams.get('roleFitJobId') ?? '';

  const roleFitFromState = (location.state as { fromRoleFit?: RoleFitCtx } | null)?.fromRoleFit;

  useEffect(() => {
    if (!roleFitFromState?.candidateId || !roleFitFromState?.jobId) return;
    try {
      sessionStorage.setItem(ROLEFIT_OPT_SESSION_KEY, JSON.stringify(roleFitFromState));
    } catch {
      /* ignore */
    }
  }, [roleFitFromState]);

  // Keep RoleFit pair ids in the URL so refresh/remounts still receive prefetch + post-save redirect.
  useEffect(() => {
    if (!roleFitFromState?.candidateId || !roleFitFromState?.jobId) return;
    if (cidParam === roleFitFromState.candidateId && jidParam === roleFitFromState.jobId) return;
    const next = new URLSearchParams(searchParams);
    next.set('roleFitCandidateId', roleFitFromState.candidateId);
    next.set('roleFitJobId', roleFitFromState.jobId);
    setSearchParams(next, { replace: true });
  }, [roleFitFromState, cidParam, jidParam, searchParams, setSearchParams]);

  // Standalone optimize (no RoleFit): drop stale persisted RoleFit context.
  useEffect(() => {
    if (cidParam || jidParam || roleFitFromState?.candidateId) return;
    clearRoleFitOptimizeSession();
  }, [cidParam, jidParam, roleFitFromState?.candidateId]);

  const effectiveRoleFit = useMemo((): RoleFitCtx | undefined => {
    if (roleFitFromState?.candidateId && roleFitFromState?.jobId) {
      return roleFitFromState;
    }
    if (cidParam && jidParam) {
      try {
        const raw = sessionStorage.getItem(ROLEFIT_OPT_SESSION_KEY);
        if (raw) {
          const p = JSON.parse(raw) as RoleFitCtx;
          if (p.candidateId === cidParam && p.jobId === jidParam) {
            return p;
          }
        }
      } catch {
        /* ignore */
      }
      return {
        candidateId: cidParam,
        jobId: jidParam,
        candidateName: '',
        jobTitle: '',
      };
    }
    return undefined;
  }, [roleFitFromState, cidParam, jidParam]);

  /** Filled from API when URL refresh dropped labels — merged into display for header. */
  const [enrichedLabels, setEnrichedLabels] = useState<{
    candidateName?: string;
    jobTitle?: string;
    clientName?: string;
  }>({});

  useEffect(() => {
    setEnrichedLabels({});
  }, [effectiveRoleFit?.candidateId, effectiveRoleFit?.jobId]);

  useEffect(() => {
    if (!effectiveRoleFit?.candidateId || !effectiveRoleFit?.jobId || !user?.orgId) return;
    const hasName = Boolean(effectiveRoleFit.candidateName?.trim());
    const hasJob = Boolean(effectiveRoleFit.jobTitle?.trim());
    if (hasName && hasJob) return;

    let cancelled = false;
    void (async () => {
      try {
        const [cand, job] = await Promise.all([
          candidateService.getById(effectiveRoleFit.candidateId!),
          jobService.getById(effectiveRoleFit.jobId!),
        ]);
        if (cancelled) return;
        setEnrichedLabels({
          candidateName: cand?.full_name?.trim() || undefined,
          jobTitle: job?.title?.trim() || undefined,
          clientName: job?.client?.name?.trim() || undefined,
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveRoleFit, user?.orgId]);

  const displayRoleFit = useMemo((): RoleFitCtx | undefined => {
    if (!effectiveRoleFit?.candidateId || !effectiveRoleFit?.jobId) return undefined;
    return {
      ...effectiveRoleFit,
      candidateName:
        effectiveRoleFit.candidateName?.trim() ||
        enrichedLabels.candidateName ||
        'Candidate',
      jobTitle: effectiveRoleFit.jobTitle?.trim() || enrichedLabels.jobTitle || 'Role',
      clientName: effectiveRoleFit.clientName?.trim() || enrichedLabels.clientName,
    };
  }, [effectiveRoleFit, enrichedLabels]);

  const returnDest = effectiveRoleFit?.returnTo ?? '/dashboard';

  const [flowStepLabel, setFlowStepLabel] = useState(() => getOptimizerFlowStepLabel(0));
  const onBreadcrumbStepChange = useCallback((label: string) => {
    setFlowStepLabel(label);
  }, []);

  const onComplete = (_result: OptimizationResult, next?: NextActionPage) => {
    switch (next) {
      case 'prepare-for-interview':
        navigate('/interviews');
        return;
      case 'ai-companion':
      case 'collaborate':
        navigate('/collaborate');
        return;
      case 'history':
        navigate('/activities');
        return;
      case 'optimize-another':
        clearRoleFitOptimizeSession();
        navigate('/single-optimize', { replace: true });
        return;
      case 'dashboard':
      default:
        clearRoleFitOptimizeSession();
        navigate(returnDest, { replace: true });
    }
  };

  const roleFitAiHref =
    displayRoleFit?.returnTo?.startsWith('/role-fit-ai') && displayRoleFit.returnTo
      ? displayRoleFit.returnTo
      : '/role-fit-ai/1-1';

  return (
    <div className="vscode-bg-primary flex min-h-0 min-h-[calc(100vh-8rem)] flex-1 flex-col overflow-hidden">
      {displayRoleFit ? (
        <PageHeader
          className="!mb-0 shrink-0 px-2 sm:px-4"
          icon={Zap}
          breadcrumb={[
            { label: 'Matching & AI', href: '/single-optimize' },
            ...(displayRoleFit.returnTo?.startsWith('/role-fit-ai')
              ? [{ label: 'RoleFit AI', href: roleFitAiHref }]
              : [{ label: 'RoleFit', href: '/role-fit' }]),
            {
              label: displayRoleFit.candidateName,
              href: `/role-fit/${displayRoleFit.candidateId}/${displayRoleFit.jobId}?return=${encodeURIComponent(returnDest)}`,
            },
            { label: flowStepLabel },
          ]}
          title={displayRoleFit.candidateName}
          subtitle={[
            displayRoleFit.jobTitle,
            displayRoleFit.clientName,
          ]
            .filter(Boolean)
            .join(' · ') || 'AI resume optimization for this pair'}
        />
      ) : (
        <PageHeader
          className="!mb-0 shrink-0 px-2 sm:px-4"
          icon={Zap}
          breadcrumb={[
            { label: 'Matching & AI', href: '/single-optimize' },
            { label: 'Single optimize' },
            { label: flowStepLabel },
          ]}
          title="Single optimize"
          subtitle={
            applicationIdFromUrl
              ? 'Optimizing in context of an application'
              : 'Upload a resume and match it to a job with AI'
          }
        />
      )}

      <div className="min-h-0 flex-1 flex flex-col overflow-hidden">
        <SingleOptimizeFlow
          key={
            effectiveRoleFit?.candidateId && effectiveRoleFit?.jobId
              ? `rf-${effectiveRoleFit.candidateId}-${effectiveRoleFit.jobId}`
              : 'so-default'
          }
          onBack={() => {
            clearRoleFitOptimizeSession();
            navigate(returnDest, { replace: true });
          }}
          onComplete={onComplete}
          isGuest={false}
          initialApplicationId={applicationIdFromUrl}
          roleFitPrefetch={
            effectiveRoleFit?.candidateId && effectiveRoleFit?.jobId
              ? {
                  candidateId: effectiveRoleFit.candidateId,
                  jobId: effectiveRoleFit.jobId,
                  returnTo: effectiveRoleFit.returnTo,
                  candidateName: effectiveRoleFit.candidateName,
                  candidateEmail: effectiveRoleFit.candidateEmail,
                  jobTitle: effectiveRoleFit.jobTitle,
                  clientName: effectiveRoleFit.clientName,
                }
              : undefined
          }
          onRoleFitAfterDownload={
            effectiveRoleFit?.candidateId && effectiveRoleFit?.jobId
              ? () => {
                  const id = effectiveRoleFit.candidateId!;
                  clearRoleFitOptimizeSession();
                  navigate(`/candidates/${id}`, { replace: true });
                }
              : undefined
          }
          onBreadcrumbStepChange={onBreadcrumbStepChange}
        />
      </div>
    </div>
  );
}
